import React, { useState, useEffect, useMemo } from 'react';

import request from '@nineeng/request';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import VATField from '../components/VATField.jsx';
import TextField from '../components/BasicMaterial.jsx';
import RadioGroup from '../components/RadioGroup.jsx';
import AutoCompleteByID from '../components/AutoCompleteByID.jsx';
import companyAnim from './assets/CompanySection.json';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import ContentContainer from '../layout/ContentContainer.jsx';

import { getTextDefaults } from './translations/companyRegistrationSection';
import { countriesEUMain } from '../util/countries';

const CONSTANTS = {
	state: {
		legalForm: 'legalForm',
	},
	radio: {
		legalFormRadio: 'legalFormRadio',
	},
	states: {
		legalForm: {
			company: 'company',
			employee: 'employee',
			selfEmployed: 'selfEmployed',
		},
	},
};

const WelcomePage = ({ text, isLoadingCompanyData }) => {
	return (
		<WelcomeScreen animation={companyAnim} animationOptions={{ loop: false }}>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.welcomeTitle}</Typography>
			<Typography>{text.welcomeReason}</Typography>
			{isLoadingCompanyData && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}>
					<CircularProgress size={20} />
					<Typography variant='body2'>Loading company information...</Typography>
				</Box>
			)}
		</WelcomeScreen>
	);
};

const VATPage = ({ values, text, onChange, isUnknownCompany, setCurrentScreen, disabled }) => {
	const Btn = <Button 
		endIcon={<ArrowForwardIcon />} 
		color='inherit'
		onClick={() => setCurrentScreen('details')}
	>
		{text.unknownCompanyBtn}
	</Button>;

	return (
		<ContentContainer>
			<Typography variant='body1' sx={{ marginBottom: 2 }}>{text.vatText}</Typography>
			<VATField
				label={text.lblVAT}
				onChange={onChange}
				name='vatNumber'
				value={values.vatNumber}
				disabled={disabled}
				required
			/>
			{isUnknownCompany && <Alert 
				severity='error' 
				action={Btn}
			>
				{text.unknownCompanyText}
			</Alert>}
		</ContentContainer>
	);
};

const ProcessingPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography sx={{ textAlign: 'center' }}><CircularProgress /></Typography>
			<Typography sx={{ textAlign: 'center' }}>Processing...</Typography>
		</WelcomeScreen>
	);
};

const DetailsPage = ({ onChange, text, values, isUnknownCompany, disabled, fieldsFromAPI, vatPreExisting }) => {
	// Show warning if API failed and VAT was pre-existing (Step 1 was skipped)
	const showWarning = isUnknownCompany && vatPreExisting;

	return (
		<WelcomeScreen>
			<Typography variant='body1' sx={{ marginBottom: 2 }}>
				{isUnknownCompany ? text.detailsUnknownText : text.detailsText}
			</Typography>
			{values.vatNumber && (
				<TextField 
					name='vatNumber' 
					label={text.lblVAT} 
					value={values.vatNumber} 
					onChange={onChange} 
					disabled={true} 
					sx={{ marginBottom: 2 }}
				/>
			)}
			{showWarning && (
				<Alert severity='warning' sx={{ marginBottom: 2 }}>
					{text.apiUnavailableWarning || 'Company information could not be loaded from the API. Please fill in the details manually.'}
				</Alert>
			)}
			<TextField 
				name='name' 
				label="Name" 
				value={values.name} 
				onChange={onChange} 
				disabled={fieldsFromAPI.name || disabled}
				sx={{ marginBottom: 2 }}
				required 
			/>
			<TextField 
				name='addressLine1' 
				label="Address" 
				value={values.addressLine1} 
				onChange={onChange} 
				disabled={fieldsFromAPI.addressLine1 || disabled} 
				sx={{ marginBottom: 2 }}
				required 
			/>
			<TextField 
				name='addressLine2' 
				label="Address - Extra" 
				value={values.addressLine2} 
				disabled={fieldsFromAPI.addressLine2 || fieldsFromAPI.addressLine1 || disabled} // also disable this if addressLine1 is from API
				onChange={onChange} 
				sx={{ marginBottom: 2 }}
			/>
			<TextField 
				name='place' 
				label="Place" 
				value={values.place} 
				onChange={onChange} 
				disabled={fieldsFromAPI.place || disabled} 
				sx={{ marginBottom: 2 }}
				required 
			/>
			<TextField 
				name='postalCode' 
				label="Postal Code" 
				value={values.postalCode} 
				disabled={fieldsFromAPI.postalCode || disabled} 
				onChange={onChange} 
				required 
				sx={{ marginBottom: 2 }}
			/>
			<AutoCompleteByID
				name='country'
				label="Country"
				value={values.country}
				options={countriesEUMain}
				optionLabel='name'
				disabled={fieldsFromAPI.country || disabled} 
				onChange={onChange}
				required
				sx={{ marginBottom: 2 }}
			/>
		</WelcomeScreen>
	);
};

const LegalFormPage = ({ onChange, text, values, disabled, radioOptions, CONSTANTS }) => {
	const handleLegalFormChange = (e) => {
		const value = e.target.value;
		// Construct event with name so handleChange can add the name prefix
		onChange({ target: { name: CONSTANTS.state.legalForm, value } });
	};

	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.legalFormHeader}</Typography>
			<Typography variant='body1' sx={{ marginBottom: 2 }}>{text.legalFormText}</Typography>
			<Box sx={{ marginTop: 5 }}>
				<RadioGroup 
					name={CONSTANTS.state.legalForm} 
					value={values[CONSTANTS.state.legalForm]}
					options={radioOptions[CONSTANTS.radio.legalFormRadio]} 
					onChange={handleLegalFormChange} 
					disabled={disabled} 
				/>
			</Box>
		</WelcomeScreen>
	);
};

const FinishedPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.finishedTitle}</Typography>
			<Typography>{text.finishedReason}</Typography>
		</WelcomeScreen>
	);
};

const CompanyRegistrationSection = ({
	onChange = () => { },
	values: formValues = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	options = {},
	name = 'groupProps',
	companyVatUrl = '/api/validate/vat',
	disabled = false,
	...rest
}) => {
	const [isUnknownCompany, setUnknownCompany] = useState(false);
	const [fieldsFromAPI, setFieldsFromAPI] = useState({});
	const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);
	const textDefaults = getTextDefaults(selectedLanguage);
	const text = Object.assign({}, textDefaults, textOverride);
	const values = formValues[name] || {};
	
	// Track if VAT was pre-existing at mount (before any user interaction)
	const vatPreExisting = useMemo(() => !!(formValues[name]?.vatNumber), []);
	
	// Always start on welcome page - navigation will skip VAT screen if needed
	const defaultPage = 'welcome';

	const radioOptions = {
		'legalFormRadio': [{
			value: 'selfEmployed',
			label: text.legalFormSelfEmployed,
		}, {
			value: 'company',
			label: text.legalFormCompany,
		}],
	};

	const checkDetails = () => {
		return !!(values.vatNumber && values.name && values.addressLine1 && values.place && values.postalCode && values.country);
	};
	
	// Helper function to create fieldsFromAPI lookup object from data
	const createFieldsFromAPI = (data) => {
		const apiFields = {};
		if (data) {
			Object.keys(data).forEach(key => {
				if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
					apiFields[key] = true;
				}
			});
		}
		return apiFields;
	};

	// Internal helper to apply companyData (same structure as API response)
	const _applyCompanyData = (companyData) => {
		// Track which fields came from companyData (same as API)
		const apiFields = createFieldsFromAPI(companyData);
		setFieldsFromAPI(apiFields);

		// Merge companyData with existing values to preserve fields not in companyData (like legalForm)
		const mergedValue = { ...values, ...companyData };
		onChange({ target: { name, value: mergedValue }});
		setUnknownCompany(false);
	};
	
	const screens = {
		welcome: {
			previous: () => navigationProps.previous(),
			next: () => {
				// Conditionally skip VAT screen if VAT is pre-existing (at mount)
				// But wait if company data is still loading
				if (vatPreExisting) {
					if (!isLoadingCompanyData) {
						setCurrentScreen('details');
					}
					// If loading, don't navigate (user will need to click next again after loading completes)
				} else {
					setCurrentScreen('vat');
				}
			},
			component: WelcomePage,
			canContinue: !isLoadingCompanyData, // Disable next button while loading
		},
		vat: {
			previous: () => setCurrentScreen('welcome'),
			next: () => handleVAT(values.vatNumber),
			component: VATPage,
			canContinue: !!values.vatNumber,
		},
		processing: {
			component: ProcessingPage,
		},
		details: {
			previous: () => {
				// Go back to VAT if not pre-existing, otherwise go to welcome
				if (vatPreExisting) {
					setCurrentScreen('welcome');
				} else {
					setCurrentScreen('vat');
				}
			},
			next: () => setCurrentScreen('legalForm'),
			component: DetailsPage,
			canContinue: checkDetails(),
		},
		legalForm: {
			previous: () => setCurrentScreen('details'),
			next: () => setCurrentScreen('finished'),
			component: LegalFormPage,
			canContinue: !!values[CONSTANTS.state.legalForm],
		},
		finished: {
			previous: () => setCurrentScreen('legalForm'),
			next: (event) => navigationProps.next(event),
			component: FinishedPage,
			canContinue: true,
		},
	};

	// Separate function to load company data without navigation (for background loading)
	// Returns true on success, false on failure
	const loadCompanyData = async vatNumber => {
		if (!vatNumber) return false;

		// First check if companyData exists - use it instead of API call
		if (values.companyData) {
			setIsLoadingCompanyData(true);
			_applyCompanyData(values.companyData);
			setIsLoadingCompanyData(false);
			return true;
		}

		setIsLoadingCompanyData(true);
		try {
			const url = `${companyVatUrl}/${vatNumber}`;
			const { body } = await request.get(url);
			console.log(body);

			// Track which fields came from API
			const apiFields = createFieldsFromAPI(body);
			setFieldsFromAPI(apiFields);

			// Merge API response with existing values to preserve fields not in API (like legalForm)
			const mergedValue = { ...values, ...body };
			onChange({ target: { name, value: mergedValue }});
			setUnknownCompany(false);
			setIsLoadingCompanyData(false);
			return true;
		} catch(error) {
			setUnknownCompany(true);
			
			// If no API response and no companyData, use groupData as defaults (but don't lock these fields)
			if (values.groupData && !values.companyData) {
				// Merge groupData as defaults - these fields remain editable
				const mergedValue = { ...values.groupData, ...values };
				onChange({ target: { name, value: mergedValue }});
				// Don't set fieldsFromAPI for groupData - fields remain editable
			} else {
				// If values already have data, preserve which fields came from API - else empty
				let existingApiFields = {};
				if (values.companyId || values.name || values.addressLine1) {
					existingApiFields = createFieldsFromAPI(values);
				}
				setFieldsFromAPI(existingApiFields);
			}
			
			setIsLoadingCompanyData(false);
			console.error(error);
			return false;
		}
	};

	const handleVAT = async vatNumber => {
		if (!vatNumber) {
			setCurrentScreen('details');
			return;
		}

		setCurrentScreen('processing');
		const success = await loadCompanyData(vatNumber);
		
		// Navigate based on result and context
		if (!success) {
			// If VAT was pre-existing and API fails: navigate to details (warning will be shown based on missing fields)
			if (vatPreExisting) {
				setCurrentScreen('details');
			} else {
				// If VAT was not pre-existing and API fails: stay on VAT screen with force continue option
				setCurrentScreen('vat');
			}
		} else {
			// Success - navigate to details
			setCurrentScreen('details');
		}
	};

	// Auto-load company data if VAT number exists on mount (without navigating)
	useEffect(() => {
		// If VAT exists, initialize fieldsFromAPI from existing values
		// (in case data was already loaded previously)
		if (vatPreExisting && values) {
			// If companyData exists, use it directly (no API call needed)
			if (values.companyData) {
				_applyCompanyData(values.companyData);
			} else {
				// No companyData - try to load from API
				loadCompanyData(values.vatNumber);
			}
		} else if (values.groupData && !vatPreExisting) {
			// If no VAT but groupData exists, use it as defaults (editable)
			const mergedValue = { ...values.groupData, ...values };
			onChange({ target: { name, value: mergedValue }});
			// Don't set fieldsFromAPI - groupData fields remain editable
		}
	}, []);

	const handleChange = ({ target }) => onChange({ target: { ...target, name: `${name}.${target.name}` }});

	const [currentScreen, setCurrentScreen] = useState(defaultPage);
	const screen = screens[currentScreen] || screens.welcome;
	
	const hasPrevious = navigationProps.hasPrevious || (currentScreen !== 'welcome');
	
	const screenProps = { 
		values, 
		onChange: handleChange, 
		text, 
		isUnknownCompany, 
		setCurrentScreen, 
		disabled, 
		radioOptions,
		fieldsFromAPI,
		vatPreExisting,
		isLoadingCompanyData,
		CONSTANTS,
	};

	return (
		<SectionContainer
			background={background}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={hasPrevious}
					hasNext={currentScreen !== 'finished' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

CompanyRegistrationSection.type = 'CompanyRegistrationSection';
CompanyRegistrationSection.hasNavigation = true;

export default CompanyRegistrationSection;